import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Card, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Tooltip, CircularProgress, Chip,
  Avatar, Paper, Alert, Divider, useTheme, alpha, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useNotifications } from '../contexts/NotificationContext';
import { useLayout } from '../contexts/LayoutContext';

/* ── Inline SVG Icons ─────────────────────────────────────────────── */
const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2L9 11M18 2l-5 16-4-9-9-4 16-5z" />
  </svg>
);
const MicIcon = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={active ? '#f44336' : 'currentColor'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="7" y="2" width="6" height="10" rx="3" /><path d="M4 10a6 6 0 0012 0M10 16v2M7 18h6" />
  </svg>
);
const StopIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><rect x="4" y="4" width="12" height="12" rx="2" /></svg>
);
const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="6" y="6" width="11" height="11" rx="2" /><path d="M3 14V5a2 2 0 012-2h9" />
  </svg>
);
const NewChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M10 4v12M4 10h12" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 6h12M8 6V4h4v2M6 6v10a1 1 0 001 1h6a1 1 0 001-1V6" />
  </svg>
);
const ChatBubbleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 4h12a2 2 0 012 2v7a2 2 0 01-2 2H8l-4 3v-3a2 2 0 01-2-2V6a2 2 0 012-2z" strokeLinejoin="round" />
  </svg>
);
const ServerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="14" height="5" rx="1.5" /><rect x="3" y="12" width="14" height="5" rx="1.5" />
    <circle cx="6" cy="5.5" r="0.75" fill="currentColor" /><circle cx="6" cy="14.5" r="0.75" fill="currentColor" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2L3 5v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V5l-7-3z" strokeLinejoin="round" />
  </svg>
);
const DollarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="10" r="7" />
    <path d="M10 6v8M12.5 8.5c0-.8-.7-1.5-1.5-1.5H9c-.8 0-1.5.7-1.5 1.5S8.2 10 9 10h2c.8 0 1.5.7 1.5 1.5s-.7 1.5-1.5 1.5H9c-.8 0-1.5-.7-1.5-1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 3L2 17h16L10 3z" strokeLinejoin="round" /><path d="M10 8v4M10 14.5v.01" strokeLinecap="round" />
  </svg>
);
const DatabaseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <ellipse cx="10" cy="5" rx="7" ry="3" /><path d="M3 5v10c0 1.66 3.13 3 7 3s7-1.34 7-3V5" /><path d="M3 10c0 1.66 3.13 3 7 3s7-1.34 7-3" />
  </svg>
);
const CloudIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5.5 15A4.5 4.5 0 015 6.1a6 6 0 0111.5 1.4A3.5 3.5 0 0115.5 15h-10z" strokeLinejoin="round" />
  </svg>
);
const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#ed6c02" strokeWidth="1.5">
    <path d="M10 3L2 17h16L10 3z" strokeLinejoin="round" /><path d="M10 8v4M10 14.5v.01" strokeLinecap="round" />
  </svg>
);
const BotAvatar = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2a4 4 0 014 4v1h1a3 3 0 013 3v4a3 3 0 01-3 3H5a3 3 0 01-3-3v-4a3 3 0 013-3h1V6a4 4 0 014-4z" strokeLinejoin="round" />
    <circle cx="7.5" cy="12" r="1.25" fill="currentColor" /><circle cx="12.5" cy="12" r="1.25" fill="currentColor" />
  </svg>
);

const ICON_MAP = { server: ServerIcon, shield: ShieldIcon, dollar: DollarIcon, alert: AlertIcon, database: DatabaseIcon, cloud: CloudIcon };

/* ── Markdown-lite renderer ────────────────────────────────────────── */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let inCodeBlock = false, codeLines = [], codeLang = '', inTable = false, tableRows = [];

  const flushTable = () => {
    if (tableRows.length < 2) { inTable = false; tableRows = []; return; }
    const headers = tableRows[0];
    const dataRows = tableRows.slice(2);
    elements.push(
      <Box key={`tbl-${elements.length}`} sx={{ overflowX: 'auto', my: 1.5 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead><tr>{headers.map((h, i) => <th key={i} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid #ddd', fontWeight: 600, whiteSpace: 'nowrap' }}>{h.trim()}</th>)}</tr></thead>
          <tbody>{dataRows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid #eee' }}>{row.map((cell, ci) => <td key={ci} style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>{inlineFormat(cell.trim())}</td>)}</tr>
          ))}</tbody>
        </table>
      </Box>
    );
    inTable = false; tableRows = [];
  };

  const inlineFormat = (str) => {
    const parts = []; let remaining = str;
    const boldRegex = /\*\*(.*?)\*\*/g; let match, lastIndex = 0;
    while ((match = boldRegex.exec(remaining)) !== null) {
      if (match.index > lastIndex) parts.push(remaining.substring(lastIndex, match.index));
      parts.push(<strong key={`b-${match.index}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) parts.push(remaining.substring(lastIndex));
    if (parts.length === 0) return str;
    return parts.map((p, i) => {
      if (typeof p !== 'string') return p;
      const codeParts = p.split(/`([^`]+)`/);
      return codeParts.map((cp, ci) => ci % 2 === 1
        ? <code key={`c-${i}-${ci}`} style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 3, fontSize: '0.85em', fontFamily: 'monospace' }}>{cp}</code>
        : cp
      );
    });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) { elements.push(<CodeBlock key={`code-${elements.length}`} code={codeLines.join('\n')} lang={codeLang} />); codeLines = []; codeLang = ''; inCodeBlock = false; }
      else { if (inTable) flushTable(); inCodeBlock = true; codeLang = line.trim().replace('```', '').trim(); }
      continue;
    }
    if (inCodeBlock) { codeLines.push(line); continue; }
    if (line.includes('|') && line.trim().startsWith('|')) { const cells = line.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1); if (!inTable) inTable = true; tableRows.push(cells); continue; } else if (inTable) { flushTable(); }
    if (line.startsWith('### ')) { elements.push(<Typography key={`h-${i}`} variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>{inlineFormat(line.slice(4))}</Typography>); continue; }
    if (line.startsWith('## ')) { elements.push(<Typography key={`h-${i}`} variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>{inlineFormat(line.slice(3))}</Typography>); continue; }
    if (line.startsWith('# ')) { elements.push(<Typography key={`h-${i}`} variant="h6" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>{inlineFormat(line.slice(2))}</Typography>); continue; }
    if (/^[\s]*[-*]\s/.test(line)) { elements.push(<Box key={`li-${i}`} sx={{ display: 'flex', gap: 1, ml: 1, my: 0.25 }}><Typography variant="body2" sx={{ lineHeight: 1.6 }}>•</Typography><Typography variant="body2" sx={{ lineHeight: 1.6 }}>{inlineFormat(line.replace(/^[\s]*[-*]\s/, ''))}</Typography></Box>); continue; }
    if (/^\s*\d+\.\s/.test(line)) { const num = line.match(/^\s*(\d+)\./)[1]; elements.push(<Box key={`ol-${i}`} sx={{ display: 'flex', gap: 1, ml: 1, my: 0.25 }}><Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 600, minWidth: 18 }}>{num}.</Typography><Typography variant="body2" sx={{ lineHeight: 1.6 }}>{inlineFormat(line.replace(/^\s*\d+\.\s/, ''))}</Typography></Box>); continue; }
    if (/^---+$/.test(line.trim())) { elements.push(<Divider key={`hr-${i}`} sx={{ my: 1.5 }} />); continue; }
    if (line.trim() === '') { elements.push(<Box key={`br-${i}`} sx={{ height: 8 }} />); continue; }
    elements.push(<Typography key={`p-${i}`} variant="body2" sx={{ lineHeight: 1.7, my: 0.25 }}>{inlineFormat(line)}</Typography>);
  }
  if (inTable) flushTable();
  if (inCodeBlock && codeLines.length) elements.push(<CodeBlock key={`code-${elements.length}`} code={codeLines.join('\n')} lang={codeLang} />);
  return elements;
}

function CodeBlock({ code, lang }) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <Box sx={{ position: 'relative', my: 1.5, borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
      {lang && <Box sx={{ px: 1.5, py: 0.5, bgcolor: alpha(theme.palette.primary.main, 0.08), fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lang}</Box>}
      <Box sx={{ position: 'absolute', top: lang ? 28 : 4, right: 4, zIndex: 1 }}>
        <Tooltip title={copied ? 'Copied' : 'Copy'}><IconButton size="small" onClick={handleCopy} sx={{ bgcolor: alpha(theme.palette.background.paper, 0.8), '&:hover': { bgcolor: theme.palette.background.paper } }}><CopyIcon /></IconButton></Tooltip>
      </Box>
      <Box component="pre" sx={{ m: 0, p: 1.5, overflowX: 'auto', fontSize: '0.78rem', lineHeight: 1.6, fontFamily: '"Google Sans Mono", monospace', bgcolor: theme.palette.mode === 'dark' ? alpha('#000', 0.3) : alpha('#000', 0.03) }}><code>{code}</code></Box>
    </Box>
  );
}

/* ── Helper: relative time ─────────────────────────────────────────── */
function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ── Main Component ────────────────────────────────────────────────── */
function AICloudAssistant() {
  const theme = useTheme();
  const { addNotification } = useNotifications();
  const { setTopBarHidden } = useLayout();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Core state
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [examplePrompts, setExamplePrompts] = useState([]);

  // Voice
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef(null);

  // Session state
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Model selection
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('claude-3-sonnet');

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', originalMessage: '' });

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    loadAccounts();
    loadExamplePrompts();
    loadModels();
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      setVoiceSupported(true);
      const r = new SR(); r.continuous = false; r.interimResults = false; r.lang = 'en-US';
      r.onresult = (e) => { setInput(prev => prev ? prev + ' ' + e.results[0][0].transcript : e.results[0][0].transcript); setIsListening(false); };
      r.onerror = () => setIsListening(false);
      r.onend = () => setIsListening(false);
      recognitionRef.current = r;
    }
    return () => { setTopBarHidden(false); };
  }, [setTopBarHidden]);

  // Load sessions when account changes
  useEffect(() => {
    if (selectedAccount) loadSessions();
  }, [selectedAccount]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadAccounts = async () => {
    try {
      const res = await fetch('/api/aws/accounts', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        const data = await res.json();
        setAwsAccounts(data.accounts || []);
        if (data.accounts?.length > 0) setSelectedAccount(data.accounts[0].id);
      }
    } catch (err) { console.error('Failed to load accounts:', err); }
  };

  const loadExamplePrompts = async () => {
    try {
      const res = await fetch('/api/ai-assistant/example-prompts', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) { const data = await res.json(); setExamplePrompts(data.prompts || []); }
    } catch (err) { console.error('Failed to load prompts:', err); }
  };

  const loadModels = async () => {
    try {
      const res = await fetch('/api/ai-assistant/models', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        const data = await res.json();
        setAvailableModels(data.models || []);
        if (data.default) setSelectedModel(data.default);
      }
    } catch (err) { console.error('Failed to load models:', err); }
  };

  const loadSessions = async () => {
    if (!selectedAccount) return;
    setSessionsLoading(true);
    try {
      const res = await fetch(`/api/ai-assistant/sessions?accountId=${selectedAccount}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) { const data = await res.json(); setSessions(data.sessions || []); }
    } catch (err) { console.error('Failed to load sessions:', err); }
    finally { setSessionsLoading(false); }
  };

  const loadSessionMessages = async (sessionId) => {
    try {
      const res = await fetch(`/api/ai-assistant/sessions/${sessionId}/messages`, { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        const data = await res.json();
        setMessages((data.messages || []).map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.created_at,
          awsDataUsed: m.aws_data_used || [],
          duration: m.duration_ms
        })));
        setActiveSessionId(sessionId);
      }
    } catch (err) { console.error('Failed to load messages:', err); }
  };

  const startNewChat = useCallback(() => {
    setActiveSessionId(null);
    setMessages([]);
    setInput('');
    setConfirmDialog({ open: false, message: '', originalMessage: '' });
    setTimeout(() => { inputRef.current?.focus(); }, 100);
  }, []);

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`/api/ai-assistant/sessions/${sessionId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (activeSessionId === sessionId) startNewChat();
      }
    } catch (err) { console.error('Failed to delete session:', err); }
  };

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      try { recognitionRef.current.stop(); } catch (_) {}
      setIsListening(false);
    } else {
      try { recognitionRef.current.abort(); } catch (_) {}
      setTimeout(() => {
        try {
          recognitionRef.current.start();
          setIsListening(true);
        } catch (err) {
          console.warn('Speech recognition start failed:', err.message);
          setIsListening(false);
        }
      }, 100);
    }
  };

  const sendMessage = useCallback(async (text, confirmedDestructive = false) => {
    const msg = text || input.trim();
    if (!msg || !selectedAccount) return;

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // If no active session, optimistically add a placeholder to the sidebar
    const isNewSession = !activeSessionId;
    if (isNewSession) {
      const tempTitle = msg.length > 60 ? msg.substring(0, 57) + '...' : msg;
      setSessions(prev => [{ id: '__pending__', title: tempTitle, updated_at: new Date().toISOString(), message_count: 1 }, ...prev.slice(0, 9)]);
    }

    try {
      const res = await fetch('/api/ai-assistant/chat', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, accountId: selectedAccount, sessionId: activeSessionId, confirmedDestructive, modelKey: selectedModel })
      });

      if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.error || `Request failed (${res.status})`); }

      const data = await res.json();

      // If backend created/returned a session, update sidebar with real ID
      if (data.sessionId) {
        setActiveSessionId(data.sessionId);
        // Replace the pending placeholder with real session, then refresh from server
        if (isNewSession) {
          setSessions(prev => prev.map(s => s.id === '__pending__' ? { ...s, id: data.sessionId } : s));
        }
        // Refresh sessions from server in background
        loadSessions();
      }

      if (data.requiresConfirmation) {
        setConfirmDialog({ open: true, message: data.reply, originalMessage: data.originalMessage });
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply, timestamp: new Date().toISOString(), guardrail: true }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant', content: data.reply, timestamp: new Date().toISOString(),
          awsDataUsed: data.awsDataUsed, duration: data.duration
        }]);
      }
    } catch (err) {
      // Remove pending placeholder on error
      if (isNewSession) {
        setSessions(prev => prev.filter(s => s.id !== '__pending__'));
      }
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I encountered an error: ${err.message}`, timestamp: new Date().toISOString(), error: true }]);
      addNotification({ type: 'error', title: 'AI Assistant Error', message: err.message });
    } finally { setLoading(false); }
  }, [input, selectedAccount, activeSessionId, selectedModel, addNotification]);

  const handleConfirm = () => { setConfirmDialog({ open: false, message: '', originalMessage: '' }); sendMessage(confirmDialog.originalMessage, true); };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const hasMessages = messages.length > 0;
  const selectedAccountName = awsAccounts.find(a => a.id === selectedAccount)?.account_name || '';

  return (
    <Box sx={{ height: 'calc(100vh - 80px)', display: 'flex', gap: 2 }}>

      {/* ══ Left Sidebar — Chat History ══════════════════════════════ */}
      <Card elevation={0} sx={{
        width: 260, minWidth: 260, flexShrink: 0, borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`, flexDirection: 'column',
        overflow: 'hidden',
        display: { xs: 'none', md: 'flex' }
      }}>
        {/* New Chat button */}
        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth variant="outlined" startIcon={<NewChatIcon />}
            onClick={startNewChat}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', py: 1,
              borderColor: theme.palette.divider, color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) }
            }}
          >
            New Chat
          </Button>
        </Box>

        <Divider />

        {/* Session list */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 0.5 }}>
          {sessionsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
          )}
          {!sessionsLoading && sessions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <Box sx={{ color: 'text.disabled', mb: 1, display: 'flex', justifyContent: 'center' }}><ChatBubbleIcon /></Box>
              <Typography variant="caption" color="text.secondary">No conversations yet. Start a new chat to begin.</Typography>
            </Box>
          )}
          {sessions.map(session => {
            const isActive = session.id === activeSessionId;
            return (
              <Box
                key={session.id}
                onClick={() => loadSessionMessages(session.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, my: 0.5,
                  borderRadius: 2, cursor: 'pointer', transition: 'all 0.12s ease',
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  border: `1px solid ${isActive ? alpha(theme.palette.primary.main, 0.2) : 'transparent'}`,
                  '&:hover': { bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.hover, 0.5),
                    '& .delete-btn': { opacity: 1 }
                  }
                }}
              >
                <Box sx={{ color: isActive ? 'primary.main' : 'text.disabled', flexShrink: 0, display: 'flex', mt: '1px' }}><ChatBubbleIcon /></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.78rem', fontWeight: isActive ? 600 : 400, color: isActive ? 'primary.main' : 'text.primary' }}>
                    {session.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {timeAgo(session.updated_at)} · {session.message_count || 0} msgs
                  </Typography>
                </Box>
                <IconButton
                  className="delete-btn" size="small"
                  onClick={(e) => deleteSession(session.id, e)}
                  sx={{ opacity: 0, transition: 'opacity 0.15s', color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                >
                  <TrashIcon />
                </IconButton>
              </Box>
            );
          })}
        </Box>

        {/* Account selector at bottom */}
        <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Account</InputLabel>
            <Select value={selectedAccount} onChange={e => { setSelectedAccount(e.target.value); startNewChat(); }} label="Account" sx={{ fontSize: '0.8rem' }}>
              {awsAccounts.map(acc => (
                <MenuItem key={acc.id} value={acc.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem', bgcolor: 'primary.main' }}>{acc.account_name?.charAt(0)?.toUpperCase()}</Avatar>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{acc.account_name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* ══ Main Chat Area ══════════════════════════════════════════ */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`, color: '#fff'
            }}><BotAvatar /></Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1.1rem' }}>Astra Cloud</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {selectedAccountName ? `Connected to ${selectedAccountName}` : 'Conversational AWS management'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Model selector */}
            {availableModels.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.78rem' }}>Model</InputLabel>
                <Select
                  value={selectedModel}
                  onChange={e => setSelectedModel(e.target.value)}
                  label="Model"
                  sx={{ fontSize: '0.78rem', borderRadius: 2, '& .MuiSelect-select': { py: '6px' } }}
                >
                  {availableModels.map(m => (
                    <MenuItem key={m.key} value={m.key}>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{m.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>{m.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* Mobile account selector */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Account</InputLabel>
                <Select value={selectedAccount} onChange={e => { setSelectedAccount(e.target.value); startNewChat(); }} label="Account">
                  {awsAccounts.map(acc => <MenuItem key={acc.id} value={acc.id}>{acc.account_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Chat card */}
        <Card elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', minHeight: 0 }}>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>

            {!hasMessages && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, py: 4 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 520 }}>
                  <Box sx={{
                    width: 52, height: 52, borderRadius: '14px', mx: 'auto', mb: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main'
                  }}><svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M10 2a4 4 0 014 4v1h1a3 3 0 013 3v4a3 3 0 01-3 3H5a3 3 0 01-3-3v-4a3 3 0 013-3h1V6a4 4 0 014-4z" strokeLinejoin="round" /><circle cx="7.5" cy="12" r="1.25" fill="currentColor" /><circle cx="12.5" cy="12" r="1.25" fill="currentColor" /></svg></Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.05rem' }}>How can I help with your AWS infrastructure?</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Retrieve metrics, investigate incidents, analyze costs, review security, and guide provisioning — grounded in your actual AWS data.
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5, width: '100%', maxWidth: 680 }}>
                  {examplePrompts.map((prompt, idx) => (
                    <Paper key={idx} elevation={0} onClick={() => sendMessage(prompt.text)}
                      sx={{ p: 1.5, borderRadius: 2, cursor: 'pointer', border: `1px solid ${theme.palette.divider}`, transition: 'all 0.15s ease',
                        '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.03), transform: 'translateY(-1px)' }
                      }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Box sx={{ color: 'primary.main', display: 'flex' }}>{ICON_MAP[prompt.icon] ? React.createElement(ICON_MAP[prompt.icon]) : <CloudIcon />}</Box>
                        <Chip label={prompt.category} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.78rem', lineHeight: 1.4 }}>{prompt.text}</Typography>
                    </Paper>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mt: 0.5 }}>
                  {['Chat + Voice', 'Safety Guardrails', 'AWS Best Practices', 'Metrics', 'Incident Investigation', 'Cost Monitoring'].map(cap => (
                    <Chip key={cap} label={cap} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 22, borderColor: alpha(theme.palette.primary.main, 0.25), color: 'text.secondary' }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Message bubbles */}
            {messages.map((msg, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 1.5 }}>
                {msg.role === 'assistant' && (
                  <Avatar sx={{ width: 28, height: 28, flexShrink: 0, mt: 0.5,
                    bgcolor: msg.error ? alpha(theme.palette.error.main, 0.1) : msg.guardrail ? alpha('#ed6c02', 0.1) : alpha(theme.palette.primary.main, 0.1),
                    color: msg.error ? 'error.main' : msg.guardrail ? '#ed6c02' : 'primary.main'
                  }}>{msg.guardrail ? <WarningIcon /> : <BotAvatar />}</Avatar>
                )}
                <Box sx={{
                  maxWidth: msg.role === 'user' ? '70%' : '85%',
                  p: msg.role === 'user' ? '10px 16px' : 0,
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : 0,
                  bgcolor: msg.role === 'user' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                }}>
                  {msg.role === 'user' ? (
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{msg.content}</Typography>
                  ) : (
                    <Box>
                      {renderMarkdown(msg.content)}
                      {msg.awsDataUsed?.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                          {msg.awsDataUsed.map(d => <Chip key={d} label={d} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />)}
                          {msg.duration && <Chip label={`${(msg.duration / 1000).toFixed(1)}s`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha(theme.palette.success.main, 0.08) }} />}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            {loading && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}><BotAvatar /></Avatar>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} thickness={5} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Querying AWS and analyzing...</Typography>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Bar */}
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.paper, 0.6) }}>
            {!selectedAccount && <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>Please select an AWS account to start chatting.</Alert>}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField inputRef={inputRef} fullWidth multiline maxRows={4}
                placeholder={selectedAccount ? 'Ask about your AWS infrastructure...' : 'Select an AWS account first'}
                value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                disabled={!selectedAccount || loading} variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', fontSize: '0.875rem', bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.03) : alpha('#000', 0.02) } }}
              />
              {voiceSupported && (
                <Tooltip title={isListening ? 'Stop listening' : 'Voice input'}>
                  <IconButton onClick={toggleVoice} disabled={!selectedAccount || loading}
                    sx={{ width: 40, height: 40, borderRadius: '10px',
                      bgcolor: isListening ? alpha('#f44336', 0.1) : 'transparent',
                      color: isListening ? '#f44336' : 'text.secondary',
                      border: `1px solid ${isListening ? '#f44336' : theme.palette.divider}`,
                      animation: isListening ? 'pulse 1.5s infinite' : 'none',
                      '@keyframes pulse': { '0%,100%': { boxShadow: '0 0 0 0 rgba(244,67,54,0.3)' }, '50%': { boxShadow: '0 0 0 8px rgba(244,67,54,0)' } }
                    }}>
                    {isListening ? <StopIcon /> : <MicIcon active={isListening} />}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Send message"><span>
                <IconButton onClick={() => sendMessage()} disabled={!input.trim() || !selectedAccount || loading}
                  sx={{ width: 40, height: 40, borderRadius: '10px',
                    bgcolor: input.trim() ? 'primary.main' : 'transparent', color: input.trim() ? '#fff' : 'text.secondary',
                    border: `1px solid ${input.trim() ? 'primary.main' : theme.palette.divider}`,
                    '&:hover': input.trim() ? { bgcolor: 'primary.dark' } : {},
                    '&.Mui-disabled': { bgcolor: 'transparent', color: 'text.disabled', border: `1px solid ${theme.palette.divider}` }
                  }}><SendIcon /></IconButton>
              </span></Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', textAlign: 'center', fontSize: '0.68rem' }}>
              Responses are based on live AWS API data. Destructive actions require explicit confirmation.
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* Destructive Action Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, message: '', originalMessage: '' })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: alpha('#ed6c02', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}><WarningIcon /></Box>
          Safety Confirmation Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>You are about to perform a potentially destructive action:</Typography>
          <Paper elevation={0} sx={{ p: 2, mt: 1.5, borderRadius: 2, bgcolor: alpha('#ed6c02', 0.05), border: '1px solid', borderColor: alpha('#ed6c02', 0.2) }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{confirmDialog.originalMessage}</Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
            This action may be irreversible. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDialog({ open: false, message: '', originalMessage: '' })} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="warning" sx={{ borderRadius: 2 }}>Yes, Proceed</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AICloudAssistant;
